import React, { useContext, useMemo, useState, useCallback } from 'react';
import { AlertTriangleIcon, CheckCircle2Icon } from 'lucide-react';
import { Button } from '../ui/button';
import { ToolCallHandlersContext } from './chart-tool-call';
import { showInConciseMode } from '../chat/threadSettings';

interface OutputTruncatedToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete' | 'interrupted'; reason?: string };
}

type Decision = 'chunks' | 'other';

function safeParse(value: unknown): any {
  if (!value) return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value as string);
  } catch {
    return null;
  }
}

/**
 * System-raised warning shown when Ellie's response was cut off at the output-token
 * limit. The agent graph injects the tool call and parks on it; picking a button
 * resumes the graph with the decision (the user can also just type a message).
 */
export const OutputTruncatedToolCall: React.FC<OutputTruncatedToolCallProps> = ({ result, status }) => {
  const handlers = useContext(ToolCallHandlersContext);
  const [submitted, setSubmitted] = useState<Decision | null>(null);

  const parsedResult = useMemo(() => safeParse(result), [result]);
  const isAwaiting = parsedResult?.status === 'awaiting_choice' || status?.type === 'interrupted';
  const decision: Decision | null =
    submitted ?? (parsedResult?.status === 'answered' ? (parsedResult.decision as Decision) : null);

  const choose = useCallback(
    (value: Decision) => {
      setSubmitted(value);
      handlers?.onResumeGraph?.({ decision: value });
    },
    [handlers]
  );

  // Bypass is off: a plain warning, no choices. The run has already ended.
  if (parsedResult?.status === 'blocked') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-700 dark:bg-amber-950/30">
        <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="text-sm m-0 font-medium text-foreground">Maximum output length reached</p>
          <p className="text-sm m-0 text-muted-foreground">
            {parsedResult.truncated_tool
              ? 'The response was cut off before it could finish, so nothing was saved from that step. '
              : 'The response was cut off mid-answer. '}
            Try asking for something smaller, or break the request into parts.
          </p>
        </div>
      </div>
    );
  }

  // Answered: collapse to a one-line record of what the user chose.
  if (decision) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20">
        <CheckCircle2Icon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm m-0 text-foreground">
          <span className="text-muted-foreground">Response was cut off — </span>
          <strong>{decision === 'chunks' ? 'trying it in smaller chunks' : 'asking something else'}</strong>
        </p>
      </div>
    );
  }

  // The user typed instead of picking, or the card was otherwise abandoned.
  if (!isAwaiting) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-2 dark:border-amber-800 dark:bg-amber-950/20">
        <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm m-0 text-muted-foreground">Response was cut off at the output limit</p>
      </div>
    );
  }

  return (
    <div className="rounded-md border border-amber-300 bg-amber-50 p-3 space-y-3 dark:border-amber-700 dark:bg-amber-950/30">
      <div className="flex items-start gap-2">
        <AlertTriangleIcon className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="space-y-0.5">
          <p className="text-sm m-0 font-medium text-foreground">This response was too long to finish</p>
          <p className="text-sm m-0 text-muted-foreground">
            {parsedResult?.truncated_tool
              ? 'It hit the maximum output length and was cut off, so nothing was saved from that step.'
              : 'It hit the maximum output length and was cut off mid-answer.'}{' '}
            How would you like to continue?
          </p>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button size="sm" onClick={() => choose('chunks')}>
          Try it in smaller chunks <span className="ml-1 opacity-70">(uses more tokens)</span>
        </Button>
        <Button size="sm" variant="outline" onClick={() => choose('other')}>
          Let me ask something else instead
        </Button>
      </div>
    </div>
  );
};

// Interactive (interrupt/resume) — must stay visible in concise mode so the user
// can actually answer.
showInConciseMode(OutputTruncatedToolCall);
